import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { TriggerContext } from './triggers'
import { defaultTriggerFactory } from './triggers'

// Re-export types
export type * from './types'

// Re-export TIME for backward compatibility
export { TIME } from './constants'

const DEFAULT_CONFIG: NotificationQueueConfig = {
  maxQueueSize: 10,
  defaultGroupConfig: {
    maxItems: 3,
    keepLatest: true,
    priority: 1
  },
  groupConfigs: {}
}

export const useNotificationQueueStore = defineStore('notificationQueue', () => {
  const router = useRouter()

  // Состояние
  const config = ref<NotificationQueueConfig>({ ...DEFAULT_CONFIG })

  // Реестр всех зарегистрированных уведомлений (не удаляются при показе)
  const notificationRegistry = ref<Record<string, QueuedNotification>>({})

  // Счетчики показов за сессию (ID -> количество показов)
  const sessionShowCounts = ref<Record<string, number>>({})

  const lastShownAt = ref<number | null>(null)
  const pageLoadedAt = ref<number>(Date.now())
  const lastActivityAt = ref<number>(Date.now())
  const currentUrl = ref<string>(!process.env.SERVER ? window.location.pathname : '')
  const isIdle = ref<boolean>(false)
  const recentEvents = ref<Array<{ type: NotificationEventType; timestamp: number; data?: any }>>([])
  const isProcessing = ref<boolean>(false)

  // Персистентное хранение - единое состояние для всех уведомлений
  const notificationStates = ref<Record<string, NotificationState>>({})

  // Вычисляемая очередь - фильтрует registry по лимиту сессии
  const queue = computed(() => {
    return Object.values(notificationRegistry.value).filter(notification => {
      // Если есть maxShowsPerSession, проверяем лимит
      if (notification.maxShowsPerSession !== undefined) {
        const sessionCount = sessionShowCounts.value[notification.id] || 0
        return sessionCount < notification.maxShowsPerSession
      }
      // Если лимита нет, показываем всегда
      return true
    })
  })

  // Получить или создать состояние уведомления
  function getNotificationState(id: string): NotificationState {
    if (!notificationStates.value[id]) {
      notificationStates.value[id] = {
        id,
        showCount: 0,
        firstShownAt: null,
        lastShownAt: null,
        showTimestamps: [],
        isBlocked: false,
        blockedAt: null,
        blockDuration: null,
        isResolved: false
      }
    }
    return notificationStates.value[id]
  }

  // Проверить, заблокировано ли уведомление
  function isNotificationBlocked(id: string): boolean {
    const state = notificationStates.value[id]
    if (!state || !state.isBlocked) return false

    // Если навсегда заблокировано
    if (state.isResolved) return true

    // Проверяем, не истекло ли время блокировки
    if (state.blockedAt && state.blockDuration) {
      const now = Date.now()
      const blockEndTime = state.blockedAt + state.blockDuration

      if (now >= blockEndTime) {
        // Время блокировки истекло
        state.isBlocked = false
        state.blockedAt = null
        state.blockDuration = null
        return false
      }
    }

    return true
  }

  // Заблокировать уведомление на время
  function blockNotification(id: string, duration: number) {
    const state = getNotificationState(id)
    state.isBlocked = true
    state.blockedAt = Date.now()
    state.blockDuration = duration
    state.isResolved = false
  }

  // Заблокировать уведомление навсегда
  function resolveNotification(id: string) {
    const state = getNotificationState(id)
    state.isBlocked = true
    state.blockedAt = Date.now()
    state.blockDuration = null
    state.isResolved = true
  }

  // Разблокировать уведомление
  function unblockNotification(id: string) {
    const state = notificationStates.value[id]
    if (state) {
      state.isBlocked = false
      state.blockedAt = null
      state.blockDuration = null
      state.isResolved = false
    }
  }

  // Обновить время блокировки
  function updateBlockDuration(id: string, newDuration: number) {
    const state = notificationStates.value[id]
    if (state && state.isBlocked && !state.isResolved) {
      state.blockDuration = newDuration
    }
  }

  // Получить историю показов
  function getShowHistory(id: string): NotificationShowHistory | null {
    const state = notificationStates.value[id]
    if (!state || state.showCount === 0) return null

    return {
      id: state.id,
      showCount: state.showCount,
      lastShownAt: state.lastShownAt || 0
    }
  }

  // Очистить историю показов
  function clearShowHistory(id: string) {
    const state = notificationStates.value[id]
    if (state) {
      state.showCount = 0
      state.firstShownAt = null
      state.lastShownAt = null
      state.showTimestamps = []
    }
  }

  // Очистить всю историю
  function clearAllShowHistory() {
    Object.values(notificationStates.value).forEach(state => {
      state.showCount = 0
      state.firstShownAt = null
      state.lastShownAt = null
      state.showTimestamps = []
    })
  }

  // Computed для обратной совместимости
  const blockedNotifications = computed(() => {
    const blocked: Record<string, BlockedNotification> = {}
    Object.entries(notificationStates.value).forEach(([id, state]) => {
      if (state.isBlocked) {
        blocked[id] = {
          id: state.id,
          blockedAt: state.blockedAt || 0,
          duration: state.blockDuration || 0,
          isResolved: state.isResolved
        }
      }
    })
    return blocked
  })

  const showHistory = computed(() => {
    const history: Record<string, NotificationShowHistory> = {}
    Object.entries(notificationStates.value).forEach(([id, state]) => {
      if (state.showCount > 0) {
        history[id] = {
          id: state.id,
          showCount: state.showCount,
          lastShownAt: state.lastShownAt || 0
        }
      }
    })
    return history
  })

  // Статистика
  const stats = ref<NotificationQueueStats>({
    totalQueued: 0,
    totalShown: 0,
    totalSkipped: 0,
    groupStats: {}
  })

  // Вычисляемые свойства
  const queueSize = computed(() => queue.value.length)
  const registrySize = computed(() => Object.keys(notificationRegistry.value).length)
  const shownInSessionCount = computed(() => {
    return Object.values(sessionShowCounts.value).reduce((sum, count) => sum + count, 0)
  })
  const idleTime = computed(() => Date.now() - lastActivityAt.value)
  const timeSincePageLoad = computed(() => Date.now() - pageLoadedAt.value)
  const timeSinceLastShown = computed(() =>
    lastShownAt.value ? Date.now() - lastShownAt.value : Infinity
  )

  // Сортированная очередь по приоритету
  const sortedQueue = computed(() => {
    return queue.value.sort((a, b) => {
      const aPriority = getEffectivePriority(a)
      const bPriority = getEffectivePriority(b)
      if (aPriority !== bPriority) return bPriority - aPriority
      return a.createdAt - b.createdAt // старые первыми при равном приоритете
    })
  })
  // Трекинг использованных триггеров для каждого уведомления
  const notificationTriggers = ref<Record<string, TrackedTrigger[]>>({})

  // Лог проверок очереди
  const processLog = ref<ProcessLogEntry[]>([])
  const MAX_LOG_ENTRIES = 100 // Максимум записей в логе

  // Debug режим (по умолчанию выключен)
  const debugMode = ref(false)

  function getEffectivePriority(notification: QueuedNotification): number {
    const groupConfig = notification.group
      ? config.value.groupConfigs[notification.group]
      : config.value.defaultGroupConfig

    return notification.priority + (groupConfig?.priority || 0)
  }

  // Создать TriggerBuilder для проверки условий с трекингом
  // Теперь использует фабрику триггеров
  function createTriggerBuilder(notificationId?: string): TriggerBuilder {
    const triggers: TrackedTrigger[] = []

    const trackTrigger = (trigger: TrackedTrigger) => {
      // Трекинг только в debug режиме
      if (debugMode.value && notificationId) {
        triggers.push(trigger)
        notificationTriggers.value[notificationId] = triggers
      }
    }

    // Создаем контекст для триггеров
    const context: TriggerContext = {
      // Временные данные
      pageLoadedAt: pageLoadedAt.value,
      lastShownAt: lastShownAt.value,
      lastActivityAt: lastActivityAt.value,
      currentTime: Date.now(),

      // URL и навигация
      currentUrl: currentUrl.value,
      currentPath: !process.env.SERVER ? window.location.pathname : '',
      currentHash: !process.env.SERVER ? window.location.hash : '',

      // Состояние системы
      notificationRegistry: notificationRegistry.value,
      notificationStates: notificationStates.value,
      sessionShowCounts: sessionShowCounts.value,
      recentEvents: recentEvents.value,

      // ID текущего уведомления
      notificationId,

      // Функции проверки
      isNotificationBlocked
    }

    // Используем фабрику для создания builder
    return defaultTriggerFactory.createBuilder(context, trackTrigger) as TriggerBuilder
  }

  // Добавить уведомление в очередь
  function addNotification(
    id: string,
    callback: (handlers: {
      handleCancel: () => void
      onResolve: () => void
    }) => void | Promise<void>,
    options: {
      group?: string
      triggerFunction?: (triggers: TriggerBuilder) => boolean | Promise<boolean>
      priority?: number
      params?: Record<string, any>
      metadata?: Record<string, any>
      cancelBlockDuration?: number // время блокировки при отказе (по умолчанию 24 часа)
      maxShows?: number // максимальное количество показов (persistent)
      maxShowsBlockDuration?: number // время блокировки при превышении maxShows
      maxShowsPerSession?: number // максимальное количество показов за сессию (non-persistent)
    } = {}
  ): string {
    // Проверяем, не заблокировано ли уведомление
    if (isNotificationBlocked(id)) {
      console.log(`Notification ${id} is blocked, skipping`)
      return id
    }

    // Проверяем, не превышено ли максимальное количество показов
    if (options.maxShows) {
      const state = notificationStates.value[id]
      if (state && state.showCount >= options.maxShows) {
        // Блокируем уведомление на время, указанное в maxShowsBlockDuration или cancelBlockDuration
        const blockDuration = options.maxShowsBlockDuration || options.cancelBlockDuration || 24 * 60 * 60 * 1000
        blockNotification(id, blockDuration)
        console.log(`Notification ${id} exceeded maxShows (${options.maxShows}), blocked for ${blockDuration}ms`)
        return id
      }
    }

    const notification: QueuedNotification = {
      id,
      group: options.group,
      callback,
      params: options.params,
      triggerFunction: options.triggerFunction,
      priority: options.priority || 1,
      createdAt: Date.now(),
      metadata: options.metadata,
      cancelBlockDuration: options.cancelBlockDuration || 24 * 60 * 60 * 1000, // 24 часа по умолчанию
      maxShows: options.maxShows,
      maxShowsBlockDuration: options.maxShowsBlockDuration,
      maxShowsPerSession: options.maxShowsPerSession
    }

    // Добавляем в реестр
    notificationRegistry.value[id] = notification

    // Применяем правила группировки ПОСЛЕ добавления
    if (notification.group) {
      applyGroupRules(notification)
    }

    // Применяем ограничение размера очереди
    if (registrySize.value > config.value.maxQueueSize) {
      // Находим самое старое уведомление
      const oldestId = Object.values(notificationRegistry.value)
        .sort((a, b) => a.createdAt - b.createdAt)[0]?.id

      if (oldestId) {
        const removed = notificationRegistry.value[oldestId]
        delete notificationRegistry.value[oldestId]
        delete sessionShowCounts.value[oldestId]
        if (removed) {
          updateStats('skipped', removed.group)
        }
      }
    }

    updateStats('queued', notification.group)

    // Запускаем обработку очереди только в продакшене
    const isTest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test'

    if (!isTest) {
      processQueue()
    }

    return id
  }

  // Применить правила группировки
  function applyGroupRules(newNotification: QueuedNotification) {
    if (!newNotification.group) return

    const groupConfig = config.value.groupConfigs[newNotification.group] || config.value.defaultGroupConfig
    const groupItems = Object.values(notificationRegistry.value).filter(n => n.group === newNotification.group)

    if (groupConfig.keepLatest && groupItems.length > 1) {
      // Удаляем все старые уведомления этой группы, кроме нового (последнего)
      const oldItems = groupItems.filter(n => n.id !== newNotification.id)
      oldItems.forEach(item => {
        delete notificationRegistry.value[item.id]
        delete sessionShowCounts.value[item.id]
        updateStats('skipped', item.group)
      })
    } else if (groupItems.length > groupConfig.maxItems) {
      // Удаляем самые старые уведомления группы, оставляя maxItems
      const sortedItems = groupItems.sort((a, b) => a.createdAt - b.createdAt)
      const toRemove = sortedItems.slice(0, groupItems.length - groupConfig.maxItems)

      toRemove.forEach(item => {
        delete notificationRegistry.value[item.id]
        delete sessionShowCounts.value[item.id]
        updateStats('skipped', item.group)
      })
    }
  }

  // Обработать очередь
  async function processQueue() {
    if (isProcessing.value || sortedQueue.value.length === 0) {
      addProcessLog({
        timestamp: Date.now(),
        action: 'skip',
        reason: isProcessing.value ? 'Already processing' : 'Queue is empty',
        queueSize: queue.value.length,
        checks: []
      })
      return
    }

    isProcessing.value = true
    const checks: NotificationCheck[] = []

    try {
      // Проверяем каждое уведомление в очереди
      for (const notification of sortedQueue.value) {
        const canShow = await canShowNotification(notification)
        const triggers = notificationTriggers.value[notification.id] || []

        checks.push({
          notificationId: notification.id,
          canShow,
          triggers,
          reason: canShow ? 'Triggers passed' : 'Triggers failed'
        })

        if (canShow) {
          // Нашли уведомление для показа
          addProcessLog({
            timestamp: Date.now(),
            action: 'show',
            notificationId: notification.id,
            queueSize: queue.value.length,
            checks
          })

          await showNotification(notification)
          return
        }
      }

      // Ни одно уведомление не прошло проверку
      addProcessLog({
        timestamp: Date.now(),
        action: 'skip',
        reason: 'No notifications passed trigger checks',
        queueSize: queue.value.length,
        checks
      })

    } catch (error) {
      console.error('Error processing notification queue:', error)
      addProcessLog({
        timestamp: Date.now(),
        action: 'error',
        reason: String(error),
        queueSize: queue.value.length,
        checks
      })
    } finally {
      isProcessing.value = false
    }
  }

  // Добавить запись в лог
  function addProcessLog(entry: ProcessLogEntry) {
    // Логирование только в debug режиме
    if (!debugMode.value) return

    processLog.value.unshift(entry)

    // Ограничиваем размер лога
    if (processLog.value.length > MAX_LOG_ENTRIES) {
      processLog.value = processLog.value.slice(0, MAX_LOG_ENTRIES)
    }
  }

  // Включить debug режим
  function enableDebug() {
    debugMode.value = true
    console.log('Notification Queue: Debug mode enabled')
  }

  // Выключить debug режим
  function disableDebug() {
    debugMode.value = false
    // Очищаем данные при выключении
    processLog.value = []
    notificationTriggers.value = {}
    console.log('Notification Queue: Debug mode disabled, data cleared')
  }

  // Найти следующее уведомление для показа
  async function findNextNotificationToShow(): Promise<QueuedNotification | null> {
    for (const notification of sortedQueue.value) {
      if (await canShowNotification(notification)) {
        return notification
      }
    }
    return null
  }

  // Проверить, можно ли показать уведомление
  async function canShowNotification(notification: QueuedNotification): Promise<boolean> {
    // КРИТИЧНО: Проверяем блокировку в первую очередь
    if (isNotificationBlocked(notification.id)) {
      return false
    }

    if (!notification.triggerFunction) {
      return true // если нет триггера, показываем сразу
    }

    try {
      const triggerBuilder = createTriggerBuilder(notification.id)
      const result = notification.triggerFunction(triggerBuilder)

      // Если результат - Promise, ждём его
      if (result instanceof Promise) {
        return await result
      }

      // Иначе возвращаем как есть
      return Boolean(result)
    } catch (error) {
      console.error('Error checking notification trigger:', error)
      return false
    }
  }

  // Показать уведомление
  async function showNotification(notification: QueuedNotification) {
    try {
      // Увеличиваем счетчик показов за сессию
      if (!sessionShowCounts.value[notification.id]) {
        sessionShowCounts.value[notification.id] = 0
      }
      sessionShowCounts.value[notification.id]++

      // Обновляем состояние уведомления
      const currentTime = Date.now()
      const state = getNotificationState(notification.id)

      if (state.showCount === 0) {
        state.firstShownAt = currentTime
      }
      state.lastShownAt = currentTime
      state.showCount++

      // Добавляем временную метку в историю
      state.showTimestamps.push(currentTime)

      // Ограничиваем размер массива (храним последние 100 показов)
      if (state.showTimestamps.length > 100) {
        state.showTimestamps = state.showTimestamps.slice(-100)
      }

      // Создаем обработчики для callback
      const handlers = {
        handleCancel: () => {
          // Блокируем уведомление на указанное время
          const duration = notification.cancelBlockDuration || 24 * 60 * 60 * 1000
          blockNotification(notification.id, duration)
          console.log(`Notification ${notification.id} cancelled, blocked for ${duration}ms`)
        },
        onResolve: () => {
          // Блокируем уведомление навсегда
          resolveNotification(notification.id)
          console.log(`Notification ${notification.id} resolved permanently`)
        }
      }

      // Выполняем callback с handlers
      await notification.callback(handlers)

      // Обновляем статистику
      lastShownAt.value = currentTime
      updateStats('shown', notification.group)

      // Планируем следующую обработку через небольшую задержку
      setTimeout(() => processQueue(), 1000)

    } catch (error) {
      console.error('Error showing notification:', error)
      updateStats('skipped', notification.group)
    }
  }

  // Обновить статистику
  function updateStats(action: 'queued' | 'shown' | 'skipped', group?: string) {
    stats.value[`total${action.charAt(0).toUpperCase() + action.slice(1)}` as keyof NotificationQueueStats]++

    if (group) {
      if (!stats.value.groupStats[group]) {
        stats.value.groupStats[group] = { queued: 0, shown: 0, skipped: 0 }
      }
      stats.value.groupStats[group][action]++
    }
  }

  // Триггерить событие
  function triggerEvent(type: NotificationEventType, data?: any) {
    recentEvents.value.push({
      type,
      timestamp: Date.now(),
      data
    })

    // Оставляем только последние 50 событий
    if (recentEvents.value.length > 50) {
      recentEvents.value = recentEvents.value.slice(-50)
    }

    // Обновляем активность
    updateActivity()

    // Запускаем обработку очереди
    processQueue()
  }

  // Обновить активность пользователя
  function updateActivity() {
    lastActivityAt.value = Date.now()
    isIdle.value = false
  }

  // Удалить уведомление из очереди
  function removeNotification(id: string): boolean {
    const notification = notificationRegistry.value[id]
    if (notification) {
      delete notificationRegistry.value[id]
      delete sessionShowCounts.value[id]
      updateStats('skipped', notification.group)
      return true
    }
    return false
  }

  // Очистить очередь
  function clearQueue(group?: string) {
    if (group) {
      Object.keys(notificationRegistry.value).forEach(id => {
        const notification = notificationRegistry.value[id]
        if (notification && notification.group === group) {
          delete notificationRegistry.value[id]
          delete sessionShowCounts.value[id]
          updateStats('skipped', notification.group)
        }
      })
    } else {
      Object.values(notificationRegistry.value).forEach(n => updateStats('skipped', n.group))
      notificationRegistry.value = {}
      sessionShowCounts.value = {}
    }
  }

  // Обновить конфигурацию
  function updateConfig(newConfig: Partial<NotificationQueueConfig>) {
    config.value = {
      ...config.value,
      ...newConfig,
      // Deep merge for nested objects
      defaultGroupConfig: newConfig.defaultGroupConfig
        ? { ...config.value.defaultGroupConfig, ...newConfig.defaultGroupConfig }
        : config.value.defaultGroupConfig,
      groupConfigs: newConfig.groupConfigs
        ? { ...config.value.groupConfigs, ...newConfig.groupConfigs }
        : config.value.groupConfigs
    }
  }

  // Зарегистрировать конфигурацию группы
  function registerGroupConfig(groupName: string, groupConfig: NotificationGroupConfig) {
    config.value.groupConfigs[groupName] = groupConfig
  }

  // Зарегистрировать несколько конфигураций групп
  function registerGroupConfigs(configs: Record<string, NotificationGroupConfig>) {
    Object.entries(configs).forEach(([groupName, groupConfig]) => {
      registerGroupConfig(groupName, groupConfig)
    })
  }

  // Удалить конфигурацию группы
  function unregisterGroupConfig(groupName: string) {
    delete config.value.groupConfigs[groupName]
  }

  // Состояние системы
  const isRunning = ref(false)
  const isPaused = ref(false)

  // Хранилище для cleanup функций
  let urlWatchStop: (() => void) | null = null
  let activityCleanup: (() => void) | null = null
  let idleIntervalId: number | null = null
  let queueIntervalId: number | null = null

  // Запустить систему уведомлений
  function start() {
    // Защита от запуска на сервере
    if (process.env.SERVER) {
      console.warn('Notification Queue: Cannot start on server side')
      return
    }

    if (isRunning.value && !isPaused.value) {
      console.warn('Notification Queue: Already running')
      return
    }

    // Если система была на паузе, просто возобновляем интервалы
    if (isPaused.value) {
      // Периодически обрабатываем очередь
      queueIntervalId = window.setInterval(() => {
        if (!isProcessing.value) {
          processQueue()
        }
      }, 10000) // каждые 10 секунд

      // Отслеживаем idle состояние
      idleIntervalId = window.setInterval(() => {
        const idleThreshold = 30000 // 30 секунд
        isIdle.value = idleTime.value > idleThreshold
      }, 5000)

      isPaused.value = false
      console.log('Notification Queue: Resumed')
      return
    }

    // Полный запуск системы
    pageLoadedAt.value = Date.now()
    lastActivityAt.value = Date.now()
    currentUrl.value = window.location.pathname

    // Отслеживаем изменения URL
    urlWatchStop = watch(() => router.currentRoute.value.path, (newPath) => {
      currentUrl.value = newPath
      triggerEvent('page_visited', { path: newPath })
    })

    // Отслеживаем активность пользователя
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
    const handleActivity = () => updateActivity()

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    // Cleanup функция для activity listeners
    activityCleanup = () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
    }

    // Отслеживаем idle состояние
    idleIntervalId = window.setInterval(() => {
      const idleThreshold = 30000 // 30 секунд
      isIdle.value = idleTime.value > idleThreshold
    }, 5000)

    // Периодически обрабатываем очередь
    queueIntervalId = window.setInterval(() => {
      if (!isProcessing.value) {
        processQueue()
      }
    }, 10000) // каждые 10 секунд

    isRunning.value = true
    isPaused.value = false
    console.log('Notification Queue: Started')
  }

  // Приостановить систему уведомлений (останавливает интервалы, но сохраняет состояние)
  function pause() {
    if (!isRunning.value || isPaused.value) {
      console.warn('Notification Queue: Not running or already paused')
      return
    }

    // Останавливаем только интервалы
    if (idleIntervalId !== null) {
      clearInterval(idleIntervalId)
      idleIntervalId = null
    }

    if (queueIntervalId !== null) {
      clearInterval(queueIntervalId)
      queueIntervalId = null
    }

    isPaused.value = true
    console.log('Notification Queue: Paused')
  }

  // Для обратной совместимости
  function init() {
    start()
  }

  // Перезапустить систему (очищает показанные в сессии, но сохраняет реестр)
  function restart() {
    console.log('Notification Queue: Restarting...')

    // Очищаем только счетчики сессии
    sessionShowCounts.value = {}

    // Если система была на паузе, возобновляем
    if (isPaused.value) {
      start()
    }

    console.log('Notification Queue: Restart completed, session cleared')
  }

  // Сбросить все состояния уведомлений (блокировки и история)
  function resetStates() {
    notificationStates.value = {}
    console.log('Notification Queue: All notification states cleared')
  }

  // Сбросить статистику
  function resetStats() {
    stats.value = {
      totalQueued: 0,
      totalShown: 0,
      totalSkipped: 0,
      groupStats: {}
    }
    console.log('Notification Queue: Statistics reset')
  }

  // Полный сброс (очередь + состояния + статистика)
  function reset() {
    clearQueue()
    resetStates()
    resetStats()
    console.log('Notification Queue: Full reset completed')
  }

  return {
    // Состояние
    queue,
    queueSize,
    registrySize,
    shownInSessionCount,
    notificationRegistry,
    sessionShowCounts, // Mutable for tests
    isIdle,
    isRunning,
    isPaused,
    stats,
    config: config, // Make config mutable for tests
    notificationStates, // Персистентное хранилище
    blockedNotifications,
    showHistory: showHistory, // Make showHistory mutable for tests
    recentEvents,
    lastActivityAt,
    pageLoadedAt,
    notificationTriggers,
    processLog,
    debugMode,

    // Вычисляемые
    timeSinceLastShown,
    timeSincePageLoad,
    idleTime,

    // Методы
    addNotification,
    removeNotification,
    clearQueue,
    triggerEvent,
    updateConfig,
    registerGroupConfig,
    registerGroupConfigs,
    unregisterGroupConfig,
    updateActivity,
    start,
    pause,
    restart,
    init, // Для обратной совместимости

    // Методы сброса
    resetStates,
    resetStats,
    reset,

    // Методы блокировки
    isNotificationBlocked,
    blockNotification,
    resolveNotification,
    unblockNotification,
    updateBlockDuration,

    // Методы работы с историей показов
    getShowHistory,
    clearShowHistory,
    clearAllShowHistory,

    // Debug методы
    enableDebug,
    disableDebug,

    // Для отладки
    processQueue,
    canShowNotification,
    showNotification,
    createTriggerBuilder
  }
}, {
  persist: {
    pick: ['notificationStates']
  }
})

// Import types from separate file
import type {
  BlockedNotification,
  NotificationCheck,
  NotificationEventType,
  NotificationGroupConfig,
  NotificationQueueConfig,
  NotificationQueueStats,
  NotificationShowHistory,
  NotificationState,
  ProcessLogEntry,
  QueuedNotification,
  TrackedTrigger,
  TriggerBuilder
} from './types'

